import { useNavigate } from "react-router-dom";
import ModalLoading from "../../../components/Formularios/Modal/ModalLoading";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Box, Card, CardContent, CircularProgress, Divider, Grid, TextField, Typography } from "@mui/material";
import ModalDetalheFilial from "./modalDetalheFilial";

import { dadosUF } from "../../../components/dadosFixos/UF";
import { dadosPais } from "../../../components/dadosFixos/Pais";

import axios from "axios";

import { Controller, useForm } from "react-hook-form";
import { EmpresaInterface } from "../../../types/EmpresaInteface";
import { endpointPrincipal } from "../../../api/endPoints/endpointPrincipal/endpointPrincipal";
import { showConfirmationDialog } from "../../../components/Formularios/Modal/ModalExcluir";
import FormTitulo from "../../../components/Formularios/Container/FormTitulo";
import FormActions from "../../../components/Formularios/Buttons/FormActions";
import CustomTextField from "../../../components/Formularios/Inputs/CustomTextField";
import CustomSelect from "../../../components/Formularios/Inputs/CustomSelect";
import SwitchAtivo from "../../../components/Formularios/Inputs/SwitchAtivo";
import ActionToolbar from "../../../components/ActionToolbar/ActionToolbar";
import CustomTable, { ColunaTabela } from "../../../components/Formularios/Table/CustomTable";
import usePaginacaoLocal from "../../../hooks/usePaginacaoLocal";
import { verificaSeAtivoERetornaBadge } from "../../../utils/verificaSeAtivoERetornaBadge/verificaSeAtivoERetornaBadge";

interface FormEmpresaProps {
  idempresa?: string;
  acao?: string;
}

// Colunas para a tabela de filiais
const colunasFiliais: ColunaTabela[] = [
  { key: "fantasia", label: "Nome Fantasia" },
  { key: "responsavel", label: "Responsável" },
  { key: "fone", label: "Telefone" },
  { key: "email", label: "Email" },
  { key: "cidade", label: "Cidade" },
  { key: "uf", label: "UF" },
  {
    key: "situacao",
    label: "Situação",
    render: (situacao) => verificaSeAtivoERetornaBadge(String(situacao)),
    align: "center",
  },
];

const opcoesUF = dadosUF.map((item) => ({ valor: item.uf, label: item.nome }));
const opcoesPais = dadosPais.map((item) => ({ valor: item.codpais, label: item.pais }));

// Extrai o motivo do erro retornado pela API (erros de validação chegam como lista)
const mensagemErro = (error: any) => {
  const mensagem = error.response?.data?.message ?? error.message;
  return Array.isArray(mensagem) ? mensagem.join(", ") : mensagem;
};

const FormEmpresa = ({ idempresa, acao }: FormEmpresaProps) => {
  const { register, handleSubmit, setValue, control, formState: { errors } } = useForm<EmpresaInterface>();

  const navigate = useNavigate();

  // Define se está no modo de visualização
  const isVisualizando = acao === 'Visualizar';

  const [loading, setLoading] = useState<boolean>(false);

  // Novo estado para controlar o modal de filiais
  const [showModalFilial, setShowModalFilial] = useState<boolean>(false);
  const [filialSelecionada, setFilialSelecionada] = useState<any>(null);
  const [acaoFilial, setAcaoFilial] = useState<string>("Novo");
  const [filiais, setFiliais] = useState<any[]>([]);

  const paginacaoFiliais = usePaginacaoLocal(filiais, 5);

  /*
    * Carrega dados da empresa para edição
    */
  const carregarEmpresaPorId = async () => {
    setLoading(true);
    try {
      const { data } = await endpointPrincipal.get(`empresas/${idempresa}`);
      setValue("id_empresa", data.id_empresa);
      setValue("cnpj", data.cnpj);
      setValue("ie", data.ie);
      setValue("fantasia", data.fantasia);
      setValue("razao_social", data.razao_social);
      setValue("logradouro", data.logradouro);
      setValue("complemento", data.complemento);
      setValue("numero", data.numero);
      setValue("bairro", data.bairro);
      setValue("cidade", data.cidade);
      setValue("cmun", data.cmun);
      setValue("cep", data.cep);
      setValue("uf", data.uf);
      setValue("cuf", data.cuf);
      setValue("cpais", data.cpais || "1058");
      setValue("email", data.email);
      setValue("situacao", data.situacao);
      setValue("fone", data.fone);
      setValue("celular", data.celular);
      setValue("site", data.site);

    } catch (error) {
      toast.error("Falha ao carregar dados. Motivo: " + error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (idempresa !== "novo") {
      carregarEmpresaPorId();
    }
  }, []);

  /**
   * Atualiza os dados do banco de dados existente por id
   */
  const atualizarEmpresaPorId = async (data: EmpresaInterface) => {
    setLoading(true);
    try {
      await endpointPrincipal.put(`empresas/${idempresa}`, data);
      toast.success(`Empresa atualizada com sucesso`);
      setTimeout(() => navigate(-1), 2000);
    } catch (error) {
      toast.error(`Falha ao atualizar empresa. Motivo: ${mensagemErro(error)}`);
    } finally {
      setLoading(false);
    }
  };
  //#endregion

  //#region Inclui os dados do banco de dados
  const incluirEmpresa = async (data: EmpresaInterface) => {
    setLoading(true);
    try {
      // Adicionando valores padrão para campos obrigatórios
      const dadosEmpresa = {
        ...data,
        situacao: data.situacao ?? 1, // 1 para ativo, 0 para inativo
        itempagina: 10, // valor padrão para itens por página
        num_filial: 1, // valor padrão para número da filial
        tempo_ocioso: 30 // valor padrão para tempo ocioso em minutos
      };

      await endpointPrincipal.post("empresas", dadosEmpresa);
      toast.success(`Empresa adicionada com sucesso`);
      setTimeout(() => navigate(-1), 2000);
    } catch (error) {
      toast.error(`Erro ao adicionar empresa. Motivo: ${mensagemErro(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (data: EmpresaInterface) => {
    if (idempresa === "novo") {
      incluirEmpresa(data);
    } else {
      atualizarEmpresaPorId(data);
    }
  };

  const buscarCep = async (cep: string) => {
    // Remove caracteres não numéricos (como hífen)
    const cepLimpo = cep.replace(/\D/g, '');

    if (cepLimpo.length < 8) {
      toast.error("CEP inválido! O CEP deve conter 8 dígitos.");
      return;
    }

    setLoading(true);

    try {
      const { data } = await axios.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);

      if (data.erro) {
        toast.error("CEP inválido ou não localizado!");
        return;
      }

      // Atualiza os campos do formulário
      setValue("logradouro", data.logradouro || "");
      setValue("complemento", data.complemento || "");
      setValue("bairro", data.bairro || "");
      setValue("cidade", data.localidade || "");
      setValue("uf", data.uf || "");

      // Se o componente usar cmun para código do município IBGE
      if (data.ibge) {
        setValue("cmun", data.ibge);
      }

      // Formata o CEP com hífen para exibição, se necessário
      const cepFormatado = cepLimpo.replace(/^(\d{5})(\d{3})$/, "$1-$2");
      setValue("cep", cepFormatado);

      toast.success("CEP localizado com sucesso!");
    } catch (erro: any) {
      console.error("Erro ao buscar CEP:", erro);
      const msg =
        erro.response?.data.message === undefined
          ? erro.message
          : erro.response.data.message;
      toast.error(`Erro ao buscar CEP. Motivo: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Definindo valores padrão ao iniciar o formulário
    if (idempresa === "novo") {
      setValue("situacao", 1);
      setValue("itempagina", 10);
      setValue("num_filial", 1);
      setValue("tempo_ocioso", 30);
      setValue("cpais", "1058");
    }
  }, []);

  // Função para formatar o CEP enquanto o usuário digita
  const formatarCep = (value: string) => {
    // Remove todos os caracteres não numéricos
    const cepLimpo = value.replace(/\D/g, '');

    // Limita a 8 dígitos
    const cepLimitado = cepLimpo.slice(0, 8);

    // Formata como 00000-000 se tiver pelo menos 6 dígitos
    if (cepLimitado.length > 5) {
      return `${cepLimitado.slice(0, 5)}-${cepLimitado.slice(5)}`;
    }

    return cepLimitado;
  };

  // Carregar filiais da empresa
  const carregarFiliais = async () => {
    if (idempresa && idempresa !== "novo") {
      try {
        const { data } = await endpointPrincipal.get(`empresas-filiais/empresa/${idempresa}`);
        setFiliais(data);
      } catch (error) {
        toast.error("Falha ao carregar filiais. Motivo: " + error);
      }
    }
  };

  useEffect(() => {
    carregarFiliais();
  }, [idempresa]);

  // Funções para manipular filiais
  const handleOpenModalFilial = () => {
    setFilialSelecionada(null);
    setAcaoFilial("Novo");
    setShowModalFilial(true);
  };

  const handleCloseModalFilial = () => {
    setShowModalFilial(false);
    setFilialSelecionada(null);
  };

  const visualizarFilial = (dados: any) => {
    setFilialSelecionada(dados);
    setAcaoFilial("Visualizar");
    setShowModalFilial(true);
  };

  const editarFilial = (dados: any) => {
    setFilialSelecionada(dados);
    setAcaoFilial("EditarItem");
    setShowModalFilial(true);
  };

  const excluirFilial = async (dados: any) => {
    await showConfirmationDialog(
      "Excluir Filial",
      `Tem certeza que deseja excluir a filial ${dados.fantasia}?`,
      "question",
      "Excluir",
      "Cancelar"
    ).then(async (resultado) => {
      if (resultado.confirmed) {
        try {
          await endpointPrincipal.delete(`empresas-filiais/${dados.id_filial}`);
          toast.success("Filial excluída com sucesso!");
          carregarFiliais();
        } catch (error) {
          toast.error("Falha ao excluir filial. Motivo: " + error);
        }
      }
    });
  };

  const handleSubmitFilial = async (dados: any) => {
    try {
      const dadosComIdEmpresa = {
        ...dados,
        id_empresa: Number(idempresa)
      };

      if (acaoFilial === "Novo") {
        await endpointPrincipal.post("empresas-filiais", dadosComIdEmpresa);
        toast.success("Filial adicionada com sucesso!");
      } else if (acaoFilial === "EditarItem") {
        await endpointPrincipal.put(`empresas-filiais/${filialSelecionada.id_filial}`, dadosComIdEmpresa);
        toast.success("Filial atualizada com sucesso!");
      }
      carregarFiliais();
      handleCloseModalFilial();
    } catch (error) {
      toast.error("Falha ao salvar filial. Motivo: " + error);
    }
  };

  return (
    <>
      <Card>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <FormTitulo titulo="Empresas" acao={acao} />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} lg={3}>
                <CustomTextField
                  label="Código Empresa"
                  name="id_empresa"
                  register={register}
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <CustomTextField
                  label="CNPJ"
                  name="cnpj"
                  register={register}
                  rules={{
                    required: "Informe o CNPJ.",
                    validate: (valor) =>
                      String(valor ?? "").replace(/\D/g, "").length === 14 ||
                      "O CNPJ deve ter 14 dígitos.",
                  }}
                  error={!!errors.cnpj}
                  helperText={errors.cnpj?.message}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <CustomTextField
                  label="Inscrição Estadual"
                  name="ie"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <SwitchAtivo name="situacao" control={control} disabled={isVisualizando} />
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomTextField
                  label="Razão Social"
                  name="razao_social"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomTextField
                  label="Nome Fantasia"
                  name="fantasia"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <CustomTextField
                  label="Telefone"
                  name="fone"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  label="Celular"
                  name="celular"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  label="Email"
                  name="email"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  label="Site"
                  name="site"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Controller
                  name="cep"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="CEP"
                      size="small"
                      fullWidth
                      value={field.value || ""}
                      onChange={(e) => {
                        const formattedValue = formatarCep(e.target.value);
                        field.onChange(formattedValue);
                      }}
                      onBlur={(e) => {
                        field.onBlur();
                        if (!isVisualizando) buscarCep(e.target.value);
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      disabled={loading || isVisualizando}
                      InputProps={{
                        endAdornment: loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomTextField
                  label="Logradouro"
                  name="logradouro"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomTextField
                  label="Complemento"
                  name="complemento"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <CustomTextField
                  label="Número"
                  name="numero"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <CustomTextField
                  label="Bairro"
                  name="bairro"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  label="Cidade"
                  name="cidade"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <CustomSelect
                  label="Estado"
                  name="uf"
                  control={control}
                  opcoes={opcoesUF}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <CustomTextField
                  label="Cod. IBGE"
                  name="cmun"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <CustomSelect
                  label="País"
                  name="cpais"
                  control={control}
                  opcoes={opcoesPais}
                  disabled={isVisualizando}
                />
              </Grid>
            </Grid>
          </CardContent>

          <Divider />
          <CardContent sx={{ pb: 0 }}>
            <Typography variant="body1">Filiais</Typography>
            <Typography variant="body2" color="text.secondary">
              A filial é opcional. A empresa pode ser salva sem esse vínculo, e a filial pode ser cadastrada depois.
            </Typography>
          </CardContent>
          {idempresa !== "novo" && (
            <>
              <ActionToolbar
                onIncluir={handleOpenModalFilial}
                disabledIncluir={isVisualizando}
                mostrarFiltrar={false}
              />
              <CustomTable
                columns={colunasFiliais}
                data={paginacaoFiliais.itensPagina}
                idField="id_filial"
                onVisualizar={visualizarFilial}
                onEditar={isVisualizando ? undefined : editarFilial}
                onDelete={isVisualizando ? undefined : excluirFilial}
                paginaAtual={paginacaoFiliais.paginaAtual}
                totalPaginas={paginacaoFiliais.totalPaginas}
                setPaginaAtual={paginacaoFiliais.setPaginaAtual}
                maxHeight="45vh"
                mensagemVazia="Nenhuma filial vinculada. O cadastro da empresa permanece válido."
              />
            </>
          )}

          <FormActions onVoltar={() => navigate(-1)} visualizar={isVisualizando} />
        </Box>
      </Card>
      <ModalLoading show={loading} label="Carregado dados da empresa ..." />
      <ModalDetalheFilial
        showModal={showModalFilial}
        acao={acaoFilial}
        onClose={handleCloseModalFilial}
        onSubmit={handleSubmitFilial}
        dadosFilial={filialSelecionada}
      />
    </>
  );
};
export default FormEmpresa;
